import { useCallback, useState } from "react";
import type { z } from "zod";
import { readFromStorage, removeFromStorage, writeToStorage } from "../utils/local-storage";

type Infer<TSchema extends z.ZodType> = z.infer<TSchema>;

export function useLocalStorage<TSchema extends z.ZodType>(key: string, initialValue: Infer<TSchema>, schema: TSchema) {
  const [storedValue, setStoredValue] = useState<Infer<TSchema>>(() => readFromStorage({ key, initialValue, schema }));

  const setValue = useCallback(
    (value: Infer<TSchema> | ((val: Infer<TSchema>) => Infer<TSchema>)) => {
      // oxlint-disable-next-line no-instanceof-builtins
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      writeToStorage({ key, value: valueToStore });
      setStoredValue(valueToStore);
    },
    [key, storedValue],
  );

  const clearValue = useCallback(() => {
    removeFromStorage({ key });
    setStoredValue(initialValue);
  }, [key, initialValue]);

  return [storedValue, setValue, clearValue] as const;
}
